
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { v4 as uuidv4 } from 'uuid';
import { format } from 'date-fns';

/**
 * Create a unique filename with timestamp and random ID
 */
export const createUniqueFilename = (baseFilename: string): string => {
  const timestamp = format(new Date(), 'yyyyMMdd-HHmmss');
  const randomId = uuidv4().slice(0, 8);
  
  const parts = baseFilename.split('.');
  const extension = parts.length > 1 ? parts.pop() : 'json';
  const name = parts.join('.');
  
  return `${name}-${timestamp}-${randomId}.${extension}`;
};

/**
 * Save a file to the app's directory
 */
export const saveFileToAppDirectory = async (filename: string, data: string): Promise<string> => {
  if (!Capacitor.isNativePlatform()) {
    // For web, return data as is
    return data;
  }
  
  try {
    const uniqueFilename = createUniqueFilename(filename);
    const path = `files/${uniqueFilename}`;
    
    // Ensure the directory exists
    try {
      await Filesystem.mkdir({
        path: 'files',
        directory: Directory.Data,
        recursive: true
      });
    } catch (e) {
      // Directory might already exist, which is fine
    }
    
    // Write the file
    await Filesystem.writeFile({
      path,
      data,
      directory: Directory.Data
    });
    
    return path;
  } catch (error) {
    console.error('Error saving file to app directory:', error);
    throw error;
  }
};

/**
 * Get a file from the app's directory
 */
export const getFileFromAppDirectory = async (filePath: string): Promise<string> => {
  if (!Capacitor.isNativePlatform()) {
    // For web, return path as is
    return filePath;
  }
  
  try {
    const result = await Filesystem.readFile({
      path: filePath,
      directory: Directory.Data
    });
    
    return result.data as string;
  } catch (error) {
    console.error('Error reading file from app directory:', error);
    throw error;
  }
};

/**
 * Copy a file from a source to a destination
 */
export const copyFile = async (
  sourcePath: string, 
  destinationPath: string, 
  sourceDirectory: Directory = Directory.Data,
  destinationDirectory: Directory = Directory.Data
): Promise<void> => {
  if (!Capacitor.isNativePlatform()) {
    return; // Not applicable for web
  }
  
  try {
    await Filesystem.copy({
      from: sourcePath,
      to: destinationPath,
      directory: sourceDirectory,
      toDirectory: destinationDirectory
    });
  } catch (error) {
    console.error('Error copying file:', error);
    throw error;
  }
};

/**
 * Delete a file from the app's directory
 */
export const deleteFile = async (filePath: string): Promise<void> => {
  if (!Capacitor.isNativePlatform()) {
    return; // Not applicable for web
  }
  
  try {
    await Filesystem.deleteFile({
      path: filePath,
      directory: Directory.Data
    });
  } catch (error) {
    console.error('Error deleting file:', error);
    throw error;
  }
};
